import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';

import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseNestedFieldDefinition } from '../../../../../core/field-definitions/entities/field-definition.entity';
import { ExtendedBaseProfileField } from '../../../../../core/profiles/entities/extended-profile-field.entity';
import { PartialRecord } from '../../../../../core/records/entities/partial-record.class';
import { throwIfNullOrUndefined } from '../../../../../helpers/typed-reactive-filters.helper';
import { ComplexListFieldValue, FieldValue } from '../../../../entities/field-values/field-type-values.entity';
import { FieldReadHelper } from '../../../../helpers/fields/field-read.helper';
import { FieldValuesToRecords } from '../../../../helpers/records/flatten-complex-list-record';
import { ChildFieldToProfileField } from '../../../../helpers/to-profile-field/to-profile-field.helper';
import { ShSearchRoutingService } from '../../../../services/search-routing.service';
import { ShBaseGenericReadonlyFieldComponent } from '../../base-generic-readonly-field/base-generic-readonly-field.component';

@Component({
    selector: 'sh-complex-list-readonly-field',
    templateUrl: './complex-list-readonly-field.component.html',
    styleUrls: ['../../base-generic-readonly-field/base-generic-readonly-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShComplexListReadonlyFieldComponent extends ShBaseGenericReadonlyFieldComponent {
    public static override readonly IS_COMPLEX: boolean = true;

    public childProfileField$!: Observable<ExtendedBaseProfileField<BaseNestedFieldDefinition>>;
    public childDefinition$!: Observable<BaseNestedFieldDefinition>;
    public childDefinitionLabel$!: Observable<string>;
    public grandChildProfileFields$!: Observable<ExtendedBaseProfileField<BaseNestedFieldDefinition>[]>;

    // Used for ThesaurusField
    public listValues!: FieldValue[] | null;

    // For recursive use of the regular ComplexMapField logic we create a partial record with a single complex value per list item
    public flattenedComplexListFieldRecords: PartialRecord[];

    private readonly cdr = inject(ChangeDetectorRef);

    constructor(
        protected fieldHelper: FieldReadHelper,
        protected searchRoutingService: ShSearchRoutingService
    ) {
        super(searchRoutingService);
        this.initChildDefinitionStreams();
    }

    protected initChildDefinitionStreams(): void {
        this.childDefinition$ = this.profileField$.pipe(
            map(profileField => {
                if (profileField.FieldDefinition.Children?.length === 0) {
                    console.error(
                        `ShComplexListReadonlyFieldComponent:: ComplexListField ${profileField.FieldDefinition.DottedKey} does not have a child definition.`
                    );
                    return [];
                }
                if (profileField.FieldDefinition.Children && profileField.FieldDefinition.Children?.length > 1) {
                    console.error(
                        `ShComplexListReadonlyFieldComponent:: ComplexListField ${profileField.FieldDefinition.DottedKey} should have only 1 child definition.`
                    );
                }
                return profileField.FieldDefinition.Children ?? [];
            }),
            map(children => children[0])
        );

        this.childProfileField$ = combineLatest([this.childDefinition$, this.profileField$]).pipe(
            map(([child, profileField]) => ChildFieldToProfileField(child, profileField))
        );

        this.childDefinitionLabel$ = this.childDefinition$.pipe(map(childDefinition => childDefinition.Label));

        const grandChildDefinitions$: Observable<BaseNestedFieldDefinition[]> = this.childDefinition$.pipe(
            map(childDefinition => childDefinition.Children),
            throwIfNullOrUndefined(`Expected ComplexField to have children`),
            map(children => {
                if (!Array.isArray(children)) {
                    throw new Error(`Expected ComplexField children to be an array but was ${children}`);
                }
                if (children.length === 0) {
                    throw new Error(`Expected ComplexField to have at least 1 child, but has ${children.length}`);
                }
                return children;
            })
        );

        this.grandChildProfileFields$ = combineLatest([grandChildDefinitions$, this.profileField$]).pipe(
            map(([children, profileField]) => children.map(child => ChildFieldToProfileField(child, profileField)))
        );
    }

    init(profileField: ExtendedBaseProfileField, record: PartialRecord): void {
        const values: ComplexListFieldValue | null = this.fieldHelper.getComplexListFieldValue(
            profileField.FieldDefinition,
            record
        );

        if (values && Object.keys(values).length > 0) {
            // Should have single nested childfield
            this.listValues = Object.values(values)[0];

            // Create separate PartialRecord for each value to use ComplexField logic
            this.flattenedComplexListFieldRecords = FieldValuesToRecords(
                this.listValues,
                (profileField.FieldDefinition.Children ?? [])[0].DottedKey
            );
        } else {
            this.listValues = [];
            this.flattenedComplexListFieldRecords = [new PartialRecord()];
        }

        this.cdr.markForCheck();
    }
}
